/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {Devices} from '@ion/core/src/types/entities';
import {BulkDeviceEditor} from '@ion/dls/src/editors/BulkDeviceEditor';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {
  editAdminDevices,
  editBulkDevices,
  editDevices
} from './state';

export interface BulkEditDeviceEditorProps {
  isCtcAdmin: boolean;
  includeDeviceType?: boolean;
  includeReportingSection?: boolean;
};

/**
 * BulkEditDeviceEditor
 */
export const BulkEditDeviceEditor = ({
  isCtcAdmin,
  includeDeviceType = false,
  includeReportingSection = false
}: BulkEditDeviceEditorProps) => {
  const dispatch = useDispatch();
  const {globalGroup}                        = useSelector(({globalGroup}) => globalGroup);
  const [editConfirm, setEditConfirm]        = useState(false);
  const [updatedDevices, setUpdatedDevices]  = useState([]);
  const {selected: selectedDevices, loading} =
    useSelector(({devices: {bulkEdit}}) => bulkEdit);

  const hasAssetAssociation = function(devices: Devices): boolean {
    let isAssociated = false;
    devices?.forEach(device => {
      isAssociated = isAssociated || !!(device.asset?.href && device.account?.title !== device?.accountUI);
    });
    return isAssociated;
  };

  function onDevicesSave(devices: Devices) {
    if (hasAssetAssociation(devices) && !isCtcAdmin) {
      setUpdatedDevices(devices);
      setEditConfirm(true);
    } else {
      if (includeDeviceType) {
        dispatch(editBulkDevices(devices));
      } else {
        dispatch(editDevices(devices));
      }
    }
  };

  function onCancel() {
    dispatch(editAdminDevices(null, false));
  };

  function onBackdropClick() {
    setEditConfirm(false);
  };

  function onDismiss() {
    setEditConfirm(false);
  };

  function onAction() {
    dispatch(editDevices(updatedDevices));
  };

  return (
    <>
      <BulkDeviceEditor open
        devices={selectedDevices}
        account={globalGroup}
        loading={loading}
        onSave={onDevicesSave}
        onCancel={onCancel}
        includeDeviceType={includeDeviceType}
        includeReportingSection={includeReportingSection} />
      {editConfirm && (
        <UserNotificationDialog show
          title={translate('common:editors.deviceEditor.confirmTitle')}
          message={`${translate('common:editors.deviceEditor.bulkConfirmMessage')}`}
          actionLabel={translate('common:editors.shared.continue')}
          dismissLabel={translate('common:editors.shared.cancel')}
          onBackdropClick={onBackdropClick}
          onDismiss={onDismiss}
          onAction={onAction} />
      )}
    </>
  );
};

export default BulkEditDeviceEditor;
